import { useState, useMemo } from "react";
import { LuChevronDown, LuSearch } from "react-icons/lu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

export type LocationCode = string | null;

type Props = {
  selectedLocation: LocationCode;
  onChange: (location: LocationCode) => void;
  className?: string;
  disabled?: boolean;
};

type LocationOption = {
  key: string;
  label: string;
  value: LocationCode;
};

const locationOptions: LocationOption[] = [
  { key: "all", label: "All", value: null },
  { key: "BR", label: "Brazil", value: "BR" },
  { key: "IN", label: "India", value: "IN" },
  { key: "GB", label: "United Kingdom", value: "GB" },
  { key: "US", label: "United States", value: "US" },
  { key: "CA", label: "Canada", value: "CA" },
  { key: "AR", label: "Argentina", value: "AR" },
  { key: "AU", label: "Australia", value: "AU" },
  { key: "AT", label: "Austria", value: "AT" },
  { key: "BE", label: "Belgium", value: "BE" },
  { key: "CL", label: "Chile", value: "CL" },
  { key: "CN", label: "China", value: "CN" },
  { key: "CO", label: "Colombia", value: "CO" },
  { key: "HR", label: "Croatia", value: "HR" },
  { key: "DK", label: "Denmark", value: "DK" },
  { key: "DO", label: "Dominican Republic", value: "DO" },
  { key: "EG", label: "Egypt", value: "EG" },
  { key: "FI", label: "Finland", value: "FI" },
  { key: "FR", label: "France", value: "FR" },
  { key: "DE", label: "Germany", value: "DE" },
  { key: "GR", label: "Greece", value: "GR" },
  { key: "HK", label: "Hong Kong", value: "HK" },
  { key: "ID", label: "Indonesia", value: "ID" },
  { key: "IE", label: "Ireland", value: "IE" },
  { key: "IL", label: "Israel", value: "IL" },
  { key: "IT", label: "Italy", value: "IT" },
  { key: "JP", label: "Japan", value: "JP" },
  { key: "JO", label: "Jordan", value: "JO" },
  { key: "KW", label: "Kuwait", value: "KW" },
  { key: "LB", label: "Lebanon", value: "LB" },
  { key: "MY", label: "Malaysia", value: "MY" },
  { key: "MX", label: "Mexico", value: "MX" },
  { key: "NL", label: "Netherlands", value: "NL" },
  { key: "NZ", label: "New Zealand", value: "NZ" },
  { key: "NG", label: "Nigeria", value: "NG" },
  { key: "NO", label: "Norway", value: "NO" },
  { key: "PK", label: "Pakistan", value: "PK" },
  { key: "PA", label: "Panama", value: "PA" },
  { key: "PE", label: "Peru", value: "PE" },
  { key: "PH", label: "Philippines", value: "PH" },
  { key: "PL", label: "Poland", value: "PL" },
  { key: "RU", label: "Russia", value: "RU" },
  { key: "SA", label: "Saudi Arabia", value: "SA" },
  { key: "RS", label: "Serbia", value: "RS" },
  { key: "SG", label: "Singapore", value: "SG" },
  { key: "ZA", label: "South Africa", value: "ZA" },
  { key: "KR", label: "South Korea", value: "KR" },
  { key: "ES", label: "Spain", value: "ES" },
  { key: "SE", label: "Sweden", value: "SE" },
  { key: "CH", label: "Switzerland", value: "CH" },
  { key: "TW", label: "Taiwan", value: "TW" },
  { key: "TH", label: "Thailand", value: "TH" },
  { key: "TR", label: "Turkey", value: "TR" },
  { key: "AE", label: "United Arab Emirates", value: "AE" },
  { key: "VE", label: "Venezuela", value: "VE" },
  { key: "PT", label: "Portugal", value: "PT" },
  { key: "LU", label: "Luxembourg", value: "LU" },
  { key: "BG", label: "Bulgaria", value: "BG" },
  { key: "CZ", label: "Czechia", value: "CZ" },
  { key: "SI", label: "Slovenia", value: "SI" },
  { key: "IS", label: "Iceland", value: "IS" },
  { key: "SK", label: "Slovakia", value: "SK" },
  { key: "LT", label: "Lithuania", value: "LT" },
  { key: "TT", label: "Trinidad and Tobago", value: "TT" },
  { key: "BD", label: "Bangladesh", value: "BD" },
  { key: "LK", label: "Sri Lanka", value: "LK" },
  { key: "KE", label: "Kenya", value: "KE" },
  { key: "HU", label: "Hungary", value: "HU" },
  { key: "MA", label: "Morocco", value: "MA" },
  { key: "CY", label: "Cyprus", value: "CY" },
  { key: "JM", label: "Jamaica", value: "JM" },
  { key: "EC", label: "Ecuador", value: "EC" },
  { key: "RO", label: "Romania", value: "RO" },
  { key: "BO", label: "Bolivia", value: "BO" },
  { key: "GT", label: "Guatemala", value: "GT" },
  { key: "CR", label: "Costa Rica", value: "CR" },
  { key: "QA", label: "Qatar", value: "QA" },
  { key: "SV", label: "El Salvador", value: "SV" },
  { key: "HN", label: "Honduras", value: "HN" },
  { key: "NI", label: "Nicaragua", value: "NI" },
  { key: "PY", label: "Paraguay", value: "PY" },
  { key: "UY", label: "Uruguay", value: "UY" },
  { key: "PR", label: "Puerto Rico", value: "PR" },
  { key: "BA", label: "Bosnia and Herzegovina", value: "BA" },
  { key: "PS", label: "Palestine", value: "PS" },
  { key: "TN", label: "Tunisia", value: "TN" },
  { key: "BH", label: "Bahrain", value: "BH" },
  { key: "VN", label: "Vietnam", value: "VN" },
  { key: "GH", label: "Ghana", value: "GH" },
  { key: "MU", label: "Mauritius", value: "MU" },
  { key: "UA", label: "Ukraine", value: "UA" },
  { key: "MT", label: "Malta", value: "MT" },
  { key: "BS", label: "Bahamas", value: "BS" },
  { key: "MV", label: "Maldives", value: "MV" },
  { key: "OM", label: "Oman", value: "OM" },
  { key: "MK", label: "North Macedonia", value: "MK" },
  { key: "LV", label: "Latvia", value: "LV" },
  { key: "EE", label: "Estonia", value: "EE" },
  { key: "IQ", label: "Iraq", value: "IQ" },
  { key: "DZ", label: "Algeria", value: "DZ" },
  { key: "AL", label: "Albania", value: "AL" },
  { key: "NP", label: "Nepal", value: "NP" },
  { key: "MO", label: "Macau", value: "MO" },
  { key: "ME", label: "Montenegro", value: "ME" },
  { key: "SN", label: "Senegal", value: "SN" },
  { key: "GE", label: "Georgia", value: "GE" },
  { key: "BN", label: "Brunei", value: "BN" },
  { key: "UG", label: "Uganda", value: "UG" },
  { key: "GP", label: "Guadeloupe", value: "GP" },
  { key: "BB", label: "Barbados", value: "BB" },
  { key: "AZ", label: "Azerbaijan", value: "AZ" },
  { key: "TZ", label: "Tanzania", value: "TZ" },
  { key: "LY", label: "Libya", value: "LY" },
  { key: "MQ", label: "Martinique", value: "MQ" },
  { key: "CM", label: "Cameroon", value: "CM" },
  { key: "BW", label: "Botswana", value: "BW" },
  { key: "ET", label: "Ethiopia", value: "ET" },
  { key: "KZ", label: "Kazakhstan", value: "KZ" },
  { key: "NA", label: "Namibia", value: "NA" },
  { key: "MG", label: "Madagascar", value: "MG" },
  { key: "NC", label: "New Caledonia", value: "NC" },
  { key: "MD", label: "Moldova", value: "MD" },
  { key: "FJ", label: "Fiji", value: "FJ" },
  { key: "BY", label: "Belarus", value: "BY" },
  { key: "JE", label: "Jersey", value: "JE" },
  { key: "GU", label: "Guam", value: "GU" },
  { key: "YE", label: "Yemen", value: "YE" },
  { key: "ZM", label: "Zambia", value: "ZM" },
  { key: "IM", label: "Isle of Man", value: "IM" },
  { key: "HT", label: "Haiti", value: "HT" },
  { key: "KH", label: "Cambodia", value: "KH" },
  { key: "AW", label: "Aruba", value: "AW" },
  { key: "PF", label: "French Polynesia", value: "PF" },
  { key: "AF", label: "Afghanistan", value: "AF" },
  { key: "BM", label: "Bermuda", value: "BM" },
  { key: "GY", label: "Guyana", value: "GY" },
  { key: "AM", label: "Armenia", value: "AM" },
  { key: "MW", label: "Malawi", value: "MW" },
  { key: "AG", label: "Antigua and Barbuda", value: "AG" },
  { key: "RW", label: "Rwanda", value: "RW" },
  { key: "GG", label: "Guernsey", value: "GG" },
  { key: "GM", label: "Gambia", value: "GM" },
  { key: "FO", label: "Faroe Islands", value: "FO" },
  { key: "LC", label: "Saint Lucia", value: "LC" },
  { key: "KY", label: "Cayman Islands", value: "KY" },
  { key: "BJ", label: "Benin", value: "BJ" },
  { key: "AD", label: "Andorra", value: "AD" },
  { key: "GD", label: "Grenada", value: "GD" },
  { key: "VI", label: "U.S. Virgin Islands", value: "VI" },
  { key: "BZ", label: "Belize", value: "BZ" },
  { key: "VC", label: "Saint Vincent and the Grenadines", value: "VC" },
  { key: "MN", label: "Mongolia", value: "MN" },
  { key: "MZ", label: "Mozambique", value: "MZ" },
  { key: "ML", label: "Mali", value: "ML" },
  { key: "AO", label: "Angola", value: "AO" },
  { key: "GF", label: "French Guiana", value: "GF" },
  { key: "UZ", label: "Uzbekistan", value: "UZ" },
  { key: "DJ", label: "Djibouti", value: "DJ" },
  { key: "BF", label: "Burkina Faso", value: "BF" },
  { key: "MC", label: "Monaco", value: "MC" },
  { key: "TG", label: "Togo", value: "TG" },
  { key: "GL", label: "Greenland", value: "GL" },
  { key: "GA", label: "Gabon", value: "GA" },
  { key: "GI", label: "Gibraltar", value: "GI" },
  { key: "CD", label: "Democratic Republic of the Congo", value: "CD" },
  { key: "KG", label: "Kyrgyzstan", value: "KG" },
  { key: "PG", label: "Papua New Guinea", value: "PG" },
  { key: "BT", label: "Bhutan", value: "BT" },
  { key: "KN", label: "Saint Kitts and Nevis", value: "KN" },
  { key: "SZ", label: "Eswatini", value: "SZ" },
  { key: "LS", label: "Lesotho", value: "LS" },
  { key: "LA", label: "Laos", value: "LA" },
  { key: "LI", label: "Liechtenstein", value: "LI" },
  { key: "MP", label: "Northern Mariana Islands", value: "MP" },
  { key: "SR", label: "Suriname", value: "SR" },
  { key: "SC", label: "Seychelles", value: "SC" },
  { key: "VG", label: "British Virgin Islands", value: "VG" },
  { key: "TC", label: "Turks and Caicos Islands", value: "TC" },
  { key: "DM", label: "Dominica", value: "DM" },
  { key: "MR", label: "Mauritania", value: "MR" },
  { key: "AX", label: "Åland Islands", value: "AX" },
  { key: "SM", label: "San Marino", value: "SM" },
  { key: "SL", label: "Sierra Leone", value: "SL" },
  { key: "NE", label: "Niger", value: "NE" },
  { key: "CG", label: "Republic of the Congo", value: "CG" },
  { key: "AI", label: "Anguilla", value: "AI" },
  { key: "YT", label: "Mayotte", value: "YT" },
  { key: "CV", label: "Cape Verde", value: "CV" },
  { key: "GN", label: "Guinea", value: "GN" },
  { key: "TM", label: "Turkmenistan", value: "TM" },
  { key: "BI", label: "Burundi", value: "BI" },
  { key: "TJ", label: "Tajikistan", value: "TJ" },
  { key: "VU", label: "Vanuatu", value: "VU" },
  { key: "SB", label: "Solomon Islands", value: "SB" },
  { key: "ER", label: "Eritrea", value: "ER" },
  { key: "WS", label: "Samoa", value: "WS" },
  { key: "AS", label: "American Samoa", value: "AS" },
  { key: "FK", label: "Falkland Islands", value: "FK" },
  { key: "GQ", label: "Equatorial Guinea", value: "GQ" },
  { key: "TO", label: "Tonga", value: "TO" },
  { key: "KM", label: "Comoros", value: "KM" },
  { key: "PW", label: "Palau", value: "PW" },
  { key: "FM", label: "Micronesia", value: "FM" },
  { key: "CF", label: "Central African Republic", value: "CF" },
  { key: "SO", label: "Somalia", value: "SO" },
  { key: "MH", label: "Marshall Islands", value: "MH" },
  { key: "VA", label: "Vatican City", value: "VA" },
  { key: "TD", label: "Chad", value: "TD" },
  { key: "KI", label: "Kiribati", value: "KI" },
  { key: "ST", label: "São Tomé and Príncipe", value: "ST" },
  { key: "TV", label: "Tuvalu", value: "TV" },
  { key: "NR", label: "Nauru", value: "NR" },
  { key: "RE", label: "Réunion", value: "RE" },
  { key: "LR", label: "Liberia", value: "LR" },
  { key: "ZW", label: "Zimbabwe", value: "ZW" },
  { key: "CI", label: "Côte d'Ivoire", value: "CI" },
  { key: "MM", label: "Myanmar", value: "MM" },
  { key: "AN", label: "Netherlands Antilles", value: "AN" },
  { key: "AQ", label: "Antarctica", value: "AQ" },
  { key: "BQ", label: "Bonaire, Sint Eustatius and Saba", value: "BQ" },
  { key: "BV", label: "Bouvet Island", value: "BV" },
  { key: "IO", label: "British Indian Ocean Territory", value: "IO" },
  { key: "CX", label: "Christmas Island", value: "CX" },
  { key: "CC", label: "Cocos (Keeling) Islands", value: "CC" },
  { key: "CK", label: "Cook Islands", value: "CK" },
  { key: "CW", label: "Curaçao", value: "CW" },
  { key: "TF", label: "French Southern Territories", value: "TF" },
  { key: "GW", label: "Guinea-Bissau", value: "GW" },
  { key: "HM", label: "Heard Island and McDonald Islands", value: "HM" },
  { key: "XK", label: "Kosovo", value: "XK" },
  { key: "MS", label: "Montserrat", value: "MS" },
  { key: "NU", label: "Niue", value: "NU" },
  { key: "NF", label: "Norfolk Island", value: "NF" },
  { key: "PN", label: "Pitcairn Islands", value: "PN" },
  { key: "BL", label: "Saint Barthélemy", value: "BL" },
  { key: "SH", label: "Saint Helena", value: "SH" },
  { key: "MF", label: "Saint Martin", value: "MF" },
  { key: "PM", label: "Saint Pierre and Miquelon", value: "PM" },
  { key: "SX", label: "Sint Maarten", value: "SX" },
  {
    key: "GS",
    label: "South Georgia and the South Sandwich Islands",
    value: "GS",
  },
  { key: "SS", label: "South Sudan", value: "SS" },
  { key: "SJ", label: "Svalbard and Jan Mayen", value: "SJ" },
  { key: "TL", label: "Timor-Leste", value: "TL" },
  { key: "TK", label: "Tokelau", value: "TK" },
  { key: "UM", label: "U.S. Minor Outlying Islands", value: "UM" },
  { key: "WF", label: "Wallis and Futuna", value: "WF" },
  { key: "EH", label: "Western Sahara", value: "EH" },
];

const LocationDropdown = ({ selectedLocation, onChange, disabled, className }: Props) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = locationOptions.find((item) => item.value === selectedLocation);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return locationOptions;

    const query = searchQuery.toLowerCase().trim();
    return locationOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(query) ||
        (option.value && option.value.toLowerCase().includes(query)),
    );
  }, [searchQuery]);

  const handleChange = (value: LocationCode) => {
    if (value !== selectedLocation) {
      onChange(value);
    }
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    // Allow Escape to close dropdown
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger
        disabled={disabled}
        className={cn(
          "max-w-[200px] flex h-9 items-center justify-between whitespace-nowrap rounded-[8px] border-[1px] border-solid border-border-gray bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-0",
          className,
        )}
      >
        <div className="mr-2 w-full flex items-center gap-2 overflow-hidden">
          <span className="shrink-0 overflow-hidden text-brand-primary-400 text-ellipsis text-[11px] font-normal leading-none tracking-[0.24px]">
            Location
          </span>
          <span
            className="flex-1 truncate text-brand-primary-800 text-[11px] font-medium leading-none tracking-[0.24px]"
            title={selectedOption?.label || "ALL"}
          >
            {selectedOption?.label || "ALL"}
          </span>
        </div>
        {!disabled && <LuChevronDown size={14} className="text-brand-primary-400" />}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="rounded-[8px] w-56">
        <div className="relative border-b border-border-gray">
          <LuSearch className="absolute left-2 top-[15px] transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search countries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="pl-8 h-8 text-sm border-0 shadow-none"
          />
        </div>

        <DropdownMenuGroup className="max-h-60 overflow-y-auto mt-1">
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500 text-center">
              No location found
            </div>
          ) : (
            filteredOptions.map((item) => (
              <DropdownMenuItem
                key={item.key}
                onSelect={() => handleChange(item.value)}
                className="cursor-pointer w-full focus:bg-gray-100"
              >
                <span className="text-brand-primary-new text-[13px] font-normal leading-[20px]">
                  {item.label}
                </span>
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LocationDropdown;
